import React, { useEffect, useRef, useState } from 'react';
import { Card, Switch } from 'antd';
import { Line } from '@antv/g2plot';
import styles from './styles/chart-placeholder.module.scss';

const AdvancesAndNonInterestIncomeChart = ({ title, data }) => {
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);
    const containerRef = useRef(null);
    const [showAdvances, setShowAdvances] = useState(true);
    const [showNonInterestIncome, setShowNonInterestIncome] = useState(true);
    const [containerWidth, setContainerWidth] = useState(550);

    useEffect(() => {
        // Function to get container width with padding consideration
        const updateWidth = () => {
            if (containerRef.current) {
                // Get the actual rendered width, accounting for padding
                const rect = containerRef.current.getBoundingClientRect();
                if (rect.width > 0) {
                    const computedStyle = window.getComputedStyle(containerRef.current);
                    const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
                    const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
                    // Use getBoundingClientRect for accurate width, subtract padding
                    const width = Math.max(rect.width - paddingLeft - paddingRight, 250);
                    setContainerWidth(width);
                } else {
                    // Fallback if rect is not available yet
                    const fallbackWidth = containerRef.current.offsetWidth || window.innerWidth || 520;
                    setContainerWidth(Math.max(fallbackWidth, 250));
                }
            }
        };

        // Initial width calculation with delay to ensure DOM is ready
        const timeout = setTimeout(updateWidth, 150);

        // ResizeObserver to watch for container size changes
        // Use requestAnimationFrame to prevent ResizeObserver loop errors
        let rafId = null;
        const resizeObserver = new ResizeObserver((entries) => {
            // Cancel any pending animation frame
            if (rafId) {
                cancelAnimationFrame(rafId);
            }

            // Schedule update in next frame to prevent loop
            rafId = requestAnimationFrame(() => {
                for (let entry of entries) {
                    // Use contentRect.width which excludes padding
                    const width = entry.contentRect.width || entry.target.getBoundingClientRect().width;
                    if (width > 0) {
                        setContainerWidth(Math.max(width, 250));
                    }
                }
                rafId = null;
            });
        });

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        // Also listen to window resize for mobile orientation changes
        window.addEventListener('resize', updateWidth);

        return () => {
            clearTimeout(timeout);
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateWidth);
        };
    }, []);

    useEffect(() => {
        // Use provided data or fallback to mock data
        const rawData = data || [
            { date: '2018-08-01', advances: 3000, nonInterestIncome: 500 },
            { date: '2018-08-02', advances: 3500, nonInterestIncome: 600 },
            { date: '2018-08-03', advances: 6000, nonInterestIncome: 800 },
            { date: '2018-08-04', advances: 4500, nonInterestIncome: 700 },
            { date: '2018-08-05', advances: 3000, nonInterestIncome: 500 },
            { date: '2018-08-06', advances: 2000, nonInterestIncome: 400 },
            { date: '2018-08-07', advances: 3500, nonInterestIncome: 600 },
            { date: '2018-08-08', advances: 4000, nonInterestIncome: 650 },
            { date: '2018-08-09', advances: 5000, nonInterestIncome: 750 },
            { date: '2018-08-10', advances: 5500, nonInterestIncome: 800 },
            { date: '2018-08-11', advances: 6000, nonInterestIncome: 900 },
            { date: '2018-08-12', advances: 4500, nonInterestIncome: 700 },
            { date: '2018-08-13', advances: 4000, nonInterestIncome: 650 },
            { date: '2018-08-14', advances: 3500, nonInterestIncome: 600 },
            { date: '2018-08-15', advances: 3000, nonInterestIncome: 500 }
        ];

        // Transform data for chart based on toggle states
        const chartData = [];
        rawData.forEach((item) => {
            if (showAdvances) {
                chartData.push({
                    date: item.date,
                    type: 'advances',
                    value: item.advances
                });
            }
            if (showNonInterestIncome) {
                chartData.push({
                    date: item.date,
                    type: 'nonInterestIncome',
                    value: item.nonInterestIncome
                });
            }
        });

        if (chartRef.current) {
            // Destroy existing chart instance if it exists
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
                chartInstanceRef.current = null;
            }

            // Get actual available width from the chart ref element
            const chartElement = chartRef.current;
            const rect = chartElement.getBoundingClientRect();
            const actualWidth = rect.width > 0 ? rect.width : containerWidth;
            const finalWidth = Math.max(actualWidth, 250); // Ensure minimum width

            // Determine if mobile
            const isMobile = window.innerWidth < 768; // Use tablet breakpoint
            const isSmallMobile = window.innerWidth < 480;

            const chartConfig = {
                data: chartData,
                xField: 'date',
                yField: 'value',
                seriesField: 'type',
                smooth: true,
                point: {
                    size: isSmallMobile ? 2 : 3,
                    shape: 'circle'
                },
                line: {
                    color: ['#1890ff', '#52c41a'],
                    size: isSmallMobile ? 1.5 : 2
                },
                tooltip: {
                    formatter: (datum) => {
                        return {
                            name: datum.type === 'advances' ? 'Total Advances' : 'Non-interest Income',
                            value: datum.value ? datum.value.toLocaleString() : '0'
                        };
                    }
                },
                yAxis: {
                    min: 0
                    // max and tickCount will auto-adjust based on data values
                },
                height: isSmallMobile ? 180 : 200,
                legend: {
                    visible: false // Hide legend since we have toggles
                }
            };

            // Use autoFit on mobile/tablet, explicit width on desktop
            if (isMobile) {
                chartConfig.autoFit = true;
            } else {
                chartConfig.width = finalWidth;
            }

            // Only create chart if we have valid dimensions
            if (finalWidth > 0 && chartElement.offsetHeight > 0) {
                try {
                    // Create new chart instance
                    chartInstanceRef.current = new Line(chartElement, chartConfig);
                    chartInstanceRef.current.render();
                } catch (error) {
                    console.error('Error rendering chart:', error);
                }
            }
        }

        // Cleanup function
        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
                chartInstanceRef.current = null;
            }
        };
    }, [data, showAdvances, showNonInterestIncome, containerWidth]);

    return (
        <Card className={styles.chartPlaceholder} title={title}>
            <div className={styles.chartContainer} ref={containerRef}>
                <div className={styles.chartToggles}>
                    <div className={styles.toggleItem}>
                        <span className={styles.toggleLabel}>Non-interest Income</span>
                        <Switch checked={showNonInterestIncome} onChange={setShowNonInterestIncome} size="small" />
                    </div>
                    <div className={styles.toggleItem}>
                        <span className={styles.toggleLabel}>Total Advances</span>
                        <Switch checked={showAdvances} onChange={setShowAdvances} size="small" />
                    </div>
                </div>
                <div ref={chartRef} style={{ width: '100%', height: '200px', minWidth: '250px', minHeight: '180px' }} />
            </div>
        </Card>
    );
};

export default AdvancesAndNonInterestIncomeChart;
